import { useState } from "react";
import { toast } from "react-toastify";
import { initialClientState, clientFormFields } from "./CreateNewForm/Clients/ClientData";
import { initialVendorState, vendorFormFields } from "./CreateNewForm/Vendor/VendorData";
import { sendEntityFormDataAction } from "../../actions/createEntityFormDataActions";
import { URL_CONFIG } from "../../config/urlConfig";

/**
 * Validation function for individual fields - validates on blur
 */
const validateField = (fieldName, value, fieldConfig) => {
    // Don't validate empty optional fields
    if (!fieldConfig?.required && (!value || value.trim() === '')) {
        return null;
    }

    // Check required validation
    if (fieldConfig?.required && (!value || value.trim() === '')) {
        return `${fieldConfig.label} is required`;
    }

    // Apply custom validation if field has validation function
    if (fieldConfig?.validation && value && value.trim() !== '') {
        const validationResult = fieldConfig.validation(value);
        if (validationResult !== true) {
            return validationResult;
        }
    }

    return null; // No error
};

/**
 * Send entity form data to API
 */
export const sendEntityFormData = async (payload) => {
    const response = await sendEntityFormDataAction(payload);
    // console.log("API Response:", response.data.data);
    if (response.status === 200 && response.data?.success === true) {
        return {
            data: response.data.data,
            status: response.data.status,
            message: response.data.data.message || "Entity created successfully"
        };
    } else {

        return {
            error: response.data?.message || "Failed to create entity",
            data: response.data,
            status: response.status
        };
    }
};

/**
 * Custom hook for managing forms data and operations
 */
export const useFormsData = () => {
    // State management
    const [activeTab, setActiveTab] = useState("client");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form data states
    const [clientFormData, setClientFormData] = useState(initialClientState);
    const [vendorFormData, setVendorFormData] = useState(initialVendorState);

    // Error states
    const [clientErrors, setClientErrors] = useState({});
    const [vendorErrors, setVendorErrors] = useState({});

    /**
     * Handle client form field changes
     */
    const handleClientChange = (name, value) => {
        // Auto-uppercase for specific fields
        const uppercaseFields = ['panNumber', 'gstNumberPrimary', 'ifscCode'];
        if (uppercaseFields.includes(name) && typeof value === 'string') {
            value = value.toUpperCase();
        }

        // Handle mobile number field - only allow numbers and limit to 10 digits
        if (name === 'contactPersonMobile' && typeof value === 'string') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }

        // Only allow numbers for account number fields
        if (name === 'accountNumber' && typeof value === 'string') {
            value = value.replace(/\D/g, '');
        }

        setClientFormData({
            ...clientFormData,
            [name]: value,
        });

        // Clear error for this field when user starts typing
        if (clientErrors[name]) {
            setClientErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    /**
     * Handle client form field blur events (validation)
     */
    const handleClientBlur = (name, value) => {
        // Find field configuration
        const fieldConfig = clientFormFields.find(f => f.name === name);

        // Validate field on blur
        const error = validateField(name, value, fieldConfig);

        if (error) {
            setClientErrors(prevErrors => ({
                ...prevErrors,
                [name]: error
            }));
        } else {
            // Clear error if validation passes
            setClientErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    /**
     * Handle vendor form field changes
     */
    const handleVendorChange = (name, value) => {
        // Auto-uppercase for specific fields
        const uppercaseFields = ['panNumber', 'gstNumberPrimary', 'ifscCode'];
        if (uppercaseFields.includes(name) && typeof value === 'string') {
            value = value.toUpperCase();
        }

        // Handle mobile number field - only allow numbers and limit to 10 digits
        if (name === 'contactPersonMobile' && typeof value === 'string') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }

        // Only allow numbers for account number fields
        if (name === 'accountNumber' && typeof value === 'string') {
            value = value.replace(/\D/g, '');
        }

        setVendorFormData({
            ...vendorFormData,
            [name]: value,
        });

        // Clear error for this field when user starts typing
        if (vendorErrors[name]) {
            setVendorErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    /**
     * Handle vendor form field blur events (validation)
     */
    const handleVendorBlur = (name, value) => {
        // Find field configuration
        const fieldConfig = vendorFormFields.find(f => f.name === name);

        // Validate field on blur
        const error = validateField(name, value, fieldConfig);

        if (error) {
            setVendorErrors(prevErrors => ({
                ...prevErrors,
                [name]: error
            }));
        } else {
            // Clear error if validation passes
            setVendorErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (data, type) => {
        setIsSubmitting(true);

        const baseUrl = URL_CONFIG.BASE_URL;
        // const urlParams = new URLSearchParams();

        // // Build URL parameters
        // if (data.entityCategory) {
        //     urlParams.append("entityCategory", data.entityCategory);
        // }
        // if (data.entityLOBType) {
        //     urlParams.append("entityLOBType", data.entityLOBType);
        // }
        // if (data.selectedlobtypenationality) {
        //     urlParams.append("selectedlobtypenationality", data.selectedlobtypenationality);
        // }
        // if (data.entityName) {
        //     urlParams.append("entityName", data.entityName);
        // }
        // if (data.isMSMERegistered) {
        //     urlParams.append("isMSMERegistered", data.isMSMERegistered);
        // }

        // Build final URL with query parameters
        const uploadUrl = `${baseUrl}/upload-documents?`;

        // Convert Yes/No to boolean values
        const convertToBoolean = (value) => {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'string') {
                return value.toLowerCase() === 'yes' || value.toLowerCase() === 'true';
            }
            return false;
        };

        // Prepare payload with entity type and convert boolean fields
        const payload = {
            entityName: data.entityName || "",
            entityAddress: data.address || "",
            aaDepartmentFunction: data.aaDepartmentFunction || "",
            natureOfBusiness: data.natureOfBusiness || "",
            entityType: type, // "client" or "vendor"
            entity: type, // Required field - same as entityType
            panNumber: data.panNumber || "",
            isGSTRegistered: convertToBoolean(data.isGSTRegistered),
            gstNumber: data.gstNumberPrimary || "",
            otherGSTNumbers: data.gstNumbersOther || "",
            isRegisteredUnderMSME: convertToBoolean(data.isRegisteredUnderMSME || data.isMSMERegistered),
            contactPerson: data.contactPersonName || "",
            designation: data.contactPersonDesignation || "",
            mobileNumber: data.contactPersonMobile || "",
            emailAddress: data.contactPersonEmail || "", // Map contactPersonEmail to emailAddress
            thirdPartyPayment: convertToBoolean(data.thirdPartyPayment),
            relationWithBeneficiary: data.relationWithBeneficiary || "",
            bankName: data.bankName || "",
            branchNameAndAddress: data.branchName || "",
            accountType: data.accountType || "",
            accountNumber: data.accountNumber || "",
            ifscCode: data.ifscCode || "",
            sendMeEmail: convertToBoolean(data.sendEmailReceipt),
            entityCategory: data.entityCategory || type,
            lobType: data.entityLOBType || data.lobType || "",
            // If LOB type is NOT "travel", default to "Domestic", otherwise use selected value
            lobNationality: data.entityLOBType !== "travel"
                ? "Domestic"
                : (data.selectedlobtypenationality || data.lobNationality || ""),
            documentUploadURL: uploadUrl,
            createdOn: new Date().toISOString(),
        };

        try {
            const response = await sendEntityFormData(payload);
            // console.log("RESPONSEING : ", response.data.data);
            if (response.data.data?.success) {
                toast.success(
                    `${type === "client" ? "Client" : "Vendor"} created successfully!`,
                    {
                        position: "top-right",
                        autoClose: 5000,
                    }
                );

                // Reset form after successful submission
                if (type === "client") {
                    setClientFormData(initialClientState);
                    setClientErrors({});
                } else {
                    setVendorFormData(initialVendorState);
                    setVendorErrors({});
                }
            } else {
                const errorMessage = response?.error || "Failed to create. Please try again.";
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            // Check if it's a CORS error
            if (error.message.includes("Failed to fetch")) {
                toast.error(
                    "Unable to connect to server. Please check your connection.",
                    {
                        position: "top-right",
                        autoClose: 5000,
                    }
                );
            } else {
                toast.error("An error occurred while submitting the form", {
                    position: "top-right",
                    autoClose: 4000,
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Handle form cancellation/reset
     */
    const handleCancel = () => {
        if (activeTab === "client") {
            setClientFormData(initialClientState);
            setClientErrors({});
        } else {
            setVendorFormData(initialVendorState);
            setVendorErrors({});
        }

        toast.info("Form cleared", {
            position: "top-right",
            autoClose: 2000,
        });
    };

    /**
     * Handle tab change between client and vendor
     */
    const handleTabChange = (tab) => {
        if (!isSubmitting) {
            setActiveTab(tab);
        }
    };

    // Return all state and handlers
    return {
        // State
        activeTab,
        isSubmitting,
        clientFormData,
        vendorFormData,
        clientErrors,
        vendorErrors,

        // Setters
        setClientErrors,
        setVendorErrors,

        // Handlers
        handleClientChange,
        handleClientBlur,
        handleVendorChange,
        handleVendorBlur,
        handleSubmit,
        handleCancel,
        handleTabChange,
    };
};